import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { verisureService } from '../services/api';
import { VerisureItem, ItemCategory } from '../types';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { VerisureQrCardModal } from '../components/verisure/VerisureQrCardModal';
import { BulkQrPrintStationModal } from '../components/verisure/BulkQrPrintStationModal';
import { useEnvironmentMode, isProductionMode } from '../services/productionModeService';
import { subscriptionService, SubscriptionUsage } from '../services/subscriptionService';
import { UpgradeModal } from '../components/subscription/UpgradeModal';

const INITIAL_DEMO_ITEMS: VerisureItem[] = [
  {
    id: 'ITM-4032',
    assetId: 'VRS-4032',
    make: 'Caterpillar',
    model: 'DE110E2 110kVA Prime Genset',
    category: 'industrial',
    ownerName: 'Dangote Agro Allied Ltd',
    conditionScore: 88,
    equipmentSize: 'industrial',
    estimatedOriginalPrice: 28500000,
    createdAt: new Date(),
    qrCodeUrl: `${window.location.origin}/item/VRS-4032`
  },
  {
    id: 'ITM-1029',
    assetId: 'VRS-1029',
    make: 'Fujikura',
    model: '90S+ Optical Fusion Splicer',
    category: 'consumer_electronics',
    ownerName: 'Apex Telecom Global Ltd',
    conditionScore: 92,
    equipmentSize: 'compact',
    estimatedOriginalPrice: 4200000,
    createdAt: new Date(),
    qrCodeUrl: `${window.location.origin}/item/VRS-1029`
  },
  {
    id: 'ITM-7718',
    assetId: 'VRS-7718',
    make: 'Schneider Electric',
    model: 'MasterPact MTZ ACB Breaker',
    category: 'electrical',
    ownerName: 'Transcorp Power Station',
    conditionScore: 95,
    equipmentSize: 'large',
    estimatedOriginalPrice: 12800000,
    createdAt: new Date(),
    qrCodeUrl: `${window.location.origin}/item/VRS-7718`
  },
  {
    id: 'ITM-6620',
    assetId: 'VRS-6620',
    make: 'Toyota',
    model: 'Hilux 2.8 GD-6 4x4',
    category: 'vehicle',
    ownerName: 'GIG Logistics Fleet',
    conditionScore: 89,
    equipmentSize: 'large',
    estimatedOriginalPrice: 45000000,
    createdAt: new Date(),
    qrCodeUrl: `${window.location.origin}/item/VRS-6620`
  },
  {
    id: 'ITM-5521',
    assetId: 'VRS-5521',
    make: 'Daikin',
    model: 'VRV-IV Air Handling System',
    category: 'home_appliance',
    ownerName: 'Bonny Medical Clinic',
    conditionScore: 86,
    equipmentSize: 'standard',
    estimatedOriginalPrice: 9500000,
    createdAt: new Date(),
    qrCodeUrl: `${window.location.origin}/item/VRS-5521`
  }
];

export const ItemsPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'master_admin'].includes(user?.role || '');
  const navigate = useNavigate();
  const { isProduction } = useEnvironmentMode();
  const [items, setItems] = useState<VerisureItem[]>(() => isProductionMode() ? [] : INITIAL_DEMO_ITEMS);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isBulkPrintOpen, setIsBulkPrintOpen] = useState(false);

  // Selected item for QR Identity Card Modal
  const [selectedCardItem, setSelectedCardItem] = useState<VerisureItem | null>(null);

  // Register Modal State
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [subscriptionUsage, setSubscriptionUsage] = useState<SubscriptionUsage | null>(null);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [newItem, setNewItem] = useState<Partial<VerisureItem>>({
    category: 'consumer_electronics',
    conditionScore: 100,
  });

  const loadUsage = async () => {
    try {
      const u = await subscriptionService.getUsageAndLimits(user);
      setSubscriptionUsage(u);
    } catch (e) {
      console.warn('Failed to load subscription usage in ItemsPage:', e);
    }
  };

  useEffect(() => {
    fetchItems();
    loadUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProduction, user]);

  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = (await verisureService.getAllItems()).data;
      if (data && data.length > 0) {
        setItems(data);
      } else {
        setItems(isProduction ? [] : INITIAL_DEMO_ITEMS);
      }
    } catch {
      setItems(isProduction ? [] : INITIAL_DEMO_ITEMS);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRegister = async () => {
    const check = await subscriptionService.canRegisterQr(user);
    if (!check.allowed) {
      setSubscriptionUsage(check.usage);
      setIsUpgradeModalOpen(true);
      toast.error(check.message || 'Freemium QR limit reached. Please upgrade to Premium.');
      return;
    }
    setIsRegisterOpen(true);
  };

  const handleRegisterItem = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const check = await subscriptionService.canRegisterQr(user);
      if (!check.allowed) {
        setSubscriptionUsage(check.usage);
        setIsUpgradeModalOpen(true);
        toast.error(check.message || 'Freemium QR limit reached. Please upgrade to Premium.');
        return;
      }

      const generatedAssetId = `ITM-${Math.floor(1000 + Math.random() * 9000)}`;
      const itemToSave = {
        ...newItem,
        assetId: generatedAssetId,
        qrCodeUrl: `${window.location.origin}/item/${generatedAssetId}`,
      };
      
      const saved = (await verisureService.registerItem(itemToSave)).data;
      setItems([saved, ...items]);
      setIsRegisterOpen(false);
      loadUsage();
      toast.success('Item registered successfully!');
      // Open the prestyled QR Card modal for immediate printing/tagging
      setSelectedCardItem(saved);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = 
      item.make?.toLowerCase().includes(search.toLowerCase()) || 
      item.model?.toLowerCase().includes(search.toLowerCase()) ||
      item.assetId?.toLowerCase().includes(search.toLowerCase()) ||
      item.ownerName?.toLowerCase().includes(search.toLowerCase());

    const matchesCategory = selectedCategory === 'all' ? true : item.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  return (
    <div className="p-3 sm:p-6 lg:p-8 max-w-7xl mx-auto font-sans space-y-6 sm:space-y-8 min-w-0 max-w-full overflow-hidden">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 sm:gap-6 min-w-0 max-w-full">
        <div className="min-w-0 max-w-full">
          <div className="inline-flex items-center gap-2 px-3 py-1 glass-pill-container text-orange-400 text-[11px] font-bold rounded-full mb-3 shadow-sm border border-orange-500/25">
            <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse"></span>
            <span className="tracking-wider uppercase">DECENTRALIZED HARDWARE VAULT</span>
          </div>
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight flex items-center gap-2.5 sm:gap-3">
            <span>Universal Item Registry</span>
            <button
              onClick={fetchItems}
              className="p-1.5 sm:p-2 glass-pill-container hover:bg-white/10 text-slate-300 rounded-full transition-all border border-white/10 active:scale-95"
              title="Refresh Data"
            >
              <svg className="w-3.5 sm:w-4 h-3.5 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </h1>
          <p className="text-slate-400 text-xs sm:text-sm mt-1.5 sm:mt-2 max-w-2xl font-normal leading-relaxed">
            Audit, track, and protect industrial machinery, electronics, and fleet assets. Each record is cryptographically bound to a physical Verisure QR Identity Card.
          </p>

          {/* Freemium Plan Usage Meter */}
          {subscriptionUsage && (
            <div className="mt-3 inline-flex flex-wrap items-center gap-2 p-1.5 px-3 rounded-full bg-slate-900/90 border border-orange-500/30 text-xs">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              <span className="text-slate-400">
                {subscriptionUsage.isOrganization ? 'Org Plan:' : 'Personal Plan:'}
              </span>
              <span className="font-bold text-orange-400 font-mono">
                {subscriptionUsage.tier === 'premium'
                  ? 'Unlimited QR Codes (Premium ⭐)'
                  : `${subscriptionUsage.currentCount} / ${subscriptionUsage.limit} Free QR Codes`}
              </span>
              {subscriptionUsage.tier !== 'premium' && (
                <button
                  type="button"
                  onClick={() => setIsUpgradeModalOpen(true)}
                  className="ml-1 text-[11px] font-bold text-amber-400 hover:text-amber-300 underline"
                >
                  Upgrade
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full md:w-auto">
          {isAdmin && (
            <button
              onClick={() => setIsBulkPrintOpen(true)}
              className="px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-full text-xs font-bold text-orange-400 glass-pill-container border border-orange-500/40 hover:bg-orange-500/10 transition-all flex items-center gap-1.5 sm:gap-2 shadow-sm shrink-0"
              title="Bulk print QR codes on transparent vinyl for appliances"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 6 2 18 2 18 9"/>
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                <rect x="6" y="14" width="12" height="8"/>
              </svg>
              <span>Bulk Print QRs</span>
            </button>
          )}
          <button 
            onClick={() => navigate('/qr-generator')}
            className="glass-pill-container hover:bg-orange-500/20 border border-orange-500/30 text-orange-400 font-bold px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-full transition flex items-center gap-1.5 sm:gap-2 text-xs shadow-sm shrink-0"
            title="Open QR Card Studio"
          >
            QR Studio
          </button>
          <button 
            onClick={handleOpenRegister}
            className="bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 hover:opacity-95 text-white font-extrabold px-4 sm:px-5 py-2 sm:py-2.5 rounded-full transition shadow-lg shadow-orange-500/25 flex items-center gap-1.5 sm:gap-2 text-xs active:scale-95 shrink-0"
          >
            <span>+</span> Register Hardware
          </button>
        </div>
      </div>

      {/* Category Capsule Pills */}
      <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-1 scrollbar-none touch-pan-x max-w-full">
        {[
          { key: 'all', label: 'All Categories' },
          { key: 'consumer_electronics', label: 'Electronics' },
          { key: 'industrial', label: 'Industrial' },
          { key: 'electrical', label: 'Electrical' },
          { key: 'home_appliance', label: 'Appliances' },
          { key: 'vehicle', label: 'Vehicles' },
          { key: 'telecom', label: 'Telecom' },
        ].map((cat) => (
          <button
            key={cat.key}
            type="button"
            onClick={() => setSelectedCategory(cat.key)}
            className={`px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs font-bold whitespace-nowrap shrink-0 transition-all ${
              selectedCategory === cat.key
                ? 'bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white shadow-md shadow-orange-500/25'
                : 'glass-pill-container text-slate-300 hover:text-white hover:bg-white/5'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Search Bar */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input 
          type="text" 
          placeholder="Search by Make, Model, Asset ID or Owner..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full fintech-input text-white rounded-2xl pl-11 pr-4 py-3.5 text-sm focus:outline-none placeholder:text-slate-500 font-medium"
        />
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400 text-sm">Synchronizing hardware registry...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map(item => (
            <div key={item.id} className="glass-card glass-card-interactive rounded-3xl p-6 border border-white/[0.08] flex flex-col justify-between">
              <div>
                <div className="flex justify-between items-start mb-4 gap-2">
                  <div className="glass-pill-container px-3 py-1 rounded-full text-[10px] font-extrabold text-orange-400 uppercase tracking-wider">
                    {item.category.replace('_', ' ')}
                  </div>
                  <button
                    onClick={() => setSelectedCardItem(item)}
                    className="px-2.5 py-1 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 text-orange-400 text-xs font-mono font-bold rounded-full transition flex items-center gap-1.5"
                    title="View Official QR Identity Card"
                  >
                    {item.assetId}
                  </button>
                </div>
                
                <h3 className="text-xl font-extrabold text-white tracking-tight leading-snug">{item.make} {item.model}</h3>
                {item.year && <p className="text-xs text-slate-400 mt-1 font-medium">Model Year: {item.year}</p>}
                
                <div className="mt-4 pt-4 border-t border-white/[0.06] space-y-2">
                  <div className="text-xs text-slate-300 flex justify-between">
                    <span className="text-slate-400 font-medium">Owner:</span> 
                    <span className="font-semibold text-white truncate max-w-[170px]">{item.ownerName}</span>
                  </div>
                  <div className="text-xs text-slate-300 flex justify-between items-center">
                    <span className="text-slate-400 font-medium">Condition Score:</span> 
                    <span className={`font-mono font-extrabold px-2 py-0.5 rounded-full text-[11px] ${
                      item.conditionScore >= 90
                        ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                        : item.conditionScore >= 75
                        ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                        : 'bg-rose-500/15 text-rose-400 border border-rose-500/30'
                    }`}>
                      {item.conditionScore}/100
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-white/[0.06] grid grid-cols-3 gap-2">
                <button 
                  onClick={() => setSelectedCardItem(item)}
                  className="bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 text-orange-400 text-xs font-bold py-2.5 rounded-xl transition flex items-center justify-center gap-1 shadow-sm"
                  title="Print & View QR Card"
                >
                  QR Card
                </button>
                <button 
                  onClick={() => navigate(`/item/${item.id || item.assetId}`)}
                  className="bg-white/5 hover:bg-white/10 text-white text-xs font-bold py-2.5 rounded-xl transition border border-white/10"
                >
                  Passport
                </button>
                <button 
                  onClick={() => navigate(`/repair/log/${item.id || item.assetId}`)}
                  className="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-bold py-2.5 rounded-xl transition border border-emerald-500/30"
                >
                  Log Repair
                </button>
              </div>
            </div>
          ))}

          {filteredItems.length === 0 && (
            <div className="col-span-full text-center py-16 text-slate-400 glass-card rounded-3xl border border-white/[0.08] border-dashed">
              <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-3 text-slate-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-slate-300">No hardware items found in the registry.</p>
              <p className="text-xs text-slate-500 mt-1">Try changing your search query or category filter.</p>
            </div>
          )}
        </div>
      )}

      {/* Verisure QR Identity Card Modal */}
      {selectedCardItem && (
        <VerisureQrCardModal
          isOpen={Boolean(selectedCardItem)}
          onClose={() => setSelectedCardItem(null)}
          value={`${window.location.origin}/item/${selectedCardItem.id || selectedCardItem.assetId}`}
          assetId={selectedCardItem.assetId}
          title={`${selectedCardItem.make} ${selectedCardItem.model}`}
          subtitle={`${selectedCardItem.category.replace('_', ' ')} • Owner: ${selectedCardItem.ownerName}`}
          size={selectedCardItem.equipmentSize || (selectedCardItem.category === 'industrial' || selectedCardItem.category === 'vehicle' ? 'large' : 'standard')}
          itemRoute={`/item/${selectedCardItem.id || selectedCardItem.assetId}`}
        />
      )}

      {/* Registration Modal */}
      {isRegisterOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-white mb-4">Register New Item</h2>
            <form onSubmit={handleRegisterItem} className="space-y-4">
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Category</label>
                  <select 
                    value={newItem.category}
                    onChange={e => setNewItem({...newItem, category: e.target.value as ItemCategory})}
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
                    required
                  >
                    <option value="consumer_electronics">Consumer Electronics</option>
                    <option value="home_appliance">Home Appliance</option>
                    <option value="vehicle">Vehicle</option>
                    <option value="industrial">Industrial Equipment</option>
                    <option value="telecom">Telecom</option>
                    <option value="electrical">Electrical Switchgear</option>
                    <option value="mechanical">Mechanical Plant</option>
                    <option value="medical">Medical Device</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Equipment Size (QR Footprint)</label>
                  <select 
                    value={newItem.equipmentSize || 'standard'}
                    onChange={e => setNewItem({...newItem, equipmentSize: e.target.value as any})}
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
                  >
                    <option value="compact">Compact (35-45mm Handheld/Tool)</option>
                    <option value="standard">Standard (75-85mm Appliance/PC)</option>
                    <option value="large">Large (120-140mm Vehicle/Rack)</option>
                    <option value="industrial">Industrial (180-220mm Plant/Genset)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Make / Brand</label>
                  <input type="text" required
                    value={newItem.make || ''} onChange={e => setNewItem({...newItem, make: e.target.value})}
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:border-orange-500" placeholder="e.g. Apple, Toyota" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Model</label>
                  <input type="text" required
                    value={newItem.model || ''} onChange={e => setNewItem({...newItem, model: e.target.value})}
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:border-orange-500" placeholder="e.g. iPhone 15, Camry" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Estimated MSRP / Original Price</label>
                  <input type="number"
                    value={newItem.estimatedOriginalPrice || ''} onChange={e => setNewItem({...newItem, estimatedOriginalPrice: Number(e.target.value)})}
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:border-orange-500" placeholder="e.g. 750000 (NGN)" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Owner Name</label>
                  <input type="text" required
                    value={newItem.ownerName || ''} onChange={e => setNewItem({...newItem, ownerName: e.target.value})}
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:border-orange-500" placeholder="John Doe or Company Ltd" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Initial Condition Score (0-100)</label>
                <input type="number" min="0" max="100" required
                  value={newItem.conditionScore || 100} onChange={e => setNewItem({...newItem, conditionScore: Number(e.target.value)})}
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:border-orange-500" />
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-800">
                <button type="button" onClick={() => setIsRegisterOpen(false)} className="px-4 py-2 text-slate-400 hover:text-white transition">Cancel</button>
                <button type="submit" className="bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white font-bold px-6 py-2 rounded-xl transition shadow-lg shadow-orange-500/20">Register & Generate QR Card</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Universal Bulk QR Print Station Modal for Registered Hardware & Appliances */}
      <BulkQrPrintStationModal
        isOpen={isBulkPrintOpen}
        onClose={() => setIsBulkPrintOpen(false)}
        items={filteredItems.map((item) => ({
          id: item.id || item.assetId,
          tagId: item.assetId,
          qrUrl: `${window.location.origin}/item/${item.assetId}`,
          title: `${item.make} ${item.model}`,
          subtitle: `Owner: ${item.ownerName}`,
          category: item.category,
        }))}
        defaultTitle="APPLIANCE & HARDWARE BULK QR PRINT STATION"
      />

      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        usage={subscriptionUsage}
        onUpgradeSuccess={() => loadUsage()}
      />
    </div>
  );
};
